import type { ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { WarningIcon } from '@phosphor-icons/react';
import type { OrderRequestedShipping } from '@skupreme/api-contracts/core';
import { Badge, Tooltip } from '@skupreme/shared/ui';
import { ProviderDropdown } from '../../legacy-bridge';
import type {
  FulfillmentProviderSelection,
  ShippingFormValues,
} from '../../lib/shipping-form';
import { CheckboxField, LabeledField, type LegacyOption } from './fields';
import { FulfillmentTypeField, SignatureOption } from './ServiceOptions';
import { ShipFromSelect, type ProviderOption } from './ShipFromSelect';

export type { ProviderOption };

export type FulfillmentCardProps = {
  control: Control<ShippingFormValues>;
  errors: FieldErrors<ShippingFormValues>;
  providers: ProviderOption[];
  isLoading?: boolean;
  currentProvider?: {
    address?: object;
    showOutOfStockBadge?: boolean;
  };
  /** The chosen warehouse quoted nothing, so there are no services to pick. */
  quoteError?: string;
  /** The warehouse list itself failed to load. */
  providerError?: string;
  /** The carrier refused to price the label we are about to buy. */
  rateError?: string;
  onProviderChange: (item: { value?: FulfillmentProviderSelection }) => void;
  /**
   * The speed and the signature are quote inputs: changing either re-prices the
   * warehouse, so they sit above the list that shows those prices. Only a
   * provider fulfillment quotes that way — a manual one prices per label.
   */
  showQuoteInputs?: boolean;
  /** Whether the quote inputs belong here is unknown until the warehouse is. */
  isProviderPending?: boolean;
  shippingSpeeds?: LegacyOption[];
  /** The marketplace's own instruction, shown under the speed it defaults to. */
  requestedShipping?: OrderRequestedShipping | null;
  isSignature?: boolean;
  onSignatureChange: (value: boolean) => void;
  /** Auto-print organizations may print from an origin of their own. */
  enableAutoPrint?: boolean;
  customOriginOptions?: { value: string; text: string; key: string }[];
  customOriginLoading?: boolean;
  onAddCustomOrigin?: (event: unknown, data: { value?: string }) => void;
  modeControl?: ReactNode;
  /** A line under the warehouse, e.g. that it is a suggestion, not yet assigned. */
  shipFromHint?: string;
  packagesSection?: ReactNode;
  /** How the order ships: the services on offer, or the manual label form. */
  children?: ReactNode;
};

/**
 * Everything the fulfillment is priced on: where it ships from, how fast, and
 * with which service. One card, because changing any of it re-prices the rest.
 */
export function FulfillmentCard({
  control,
  errors,
  providers,
  isLoading,
  currentProvider,
  quoteError,
  providerError,
  rateError,
  onProviderChange,
  showQuoteInputs,
  isProviderPending,
  shippingSpeeds = [],
  requestedShipping,
  isSignature,
  onSignatureChange,
  enableAutoPrint,
  customOriginOptions = [],
  customOriginLoading,
  onAddCustomOrigin,
  modeControl,
  shipFromHint,
  packagesSection,
  children,
}: FulfillmentCardProps) {
  const useCustomOrigin = useWatch({ control, name: 'custom_origin' });

  return (
    <div className="sk:flex sk:flex-col sk:gap-4 sk:border-t sk:border-rule sk:py-4">
      {currentProvider?.showOutOfStockBadge ||
      quoteError ||
      rateError ||
      providerError ? (
        <span className="sk:flex sk:items-center sk:justify-end sk:gap-2">
          {currentProvider?.showOutOfStockBadge ? (
            <Badge tone="caution">Out of stock</Badge>
          ) : null}
          {quoteError ? (
            <Tooltip content={quoteError}>
              <Badge tone="warning">
                <WarningIcon size={12} weight="bold" />
                No rates
              </Badge>
            </Tooltip>
          ) : null}
          {rateError ? (
            <Tooltip content={rateError}>
              <Badge tone="danger">
                <WarningIcon size={12} weight="bold" />
                No rate
              </Badge>
            </Tooltip>
          ) : null}
          {providerError ? (
            <Tooltip content={providerError}>
              <Badge tone="danger">
                <WarningIcon size={12} weight="bold" />
                Error
              </Badge>
            </Tooltip>
          ) : null}
        </span>
      ) : null}

      <div className="sk:flex sk:flex-col sk:gap-3">
        {isProviderPending ? (
          <>
            <div className="sk:h-9 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
            <div className="sk:h-5 sk:w-40 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
          </>
        ) : null}

        {showQuoteInputs ? (
          <div className="sk:flex sk:flex-col sk:gap-2">
            <FulfillmentTypeField
              control={control}
              errors={errors}
              options={shippingSpeeds}
              requested={requestedShipping}
            />
            <SignatureOption
              isSelected={!!isSignature}
              onChange={onSignatureChange}
            />
          </div>
        ) : null}

        <Controller
          control={control}
          name="fulfillment_provider_id"
          render={({ field }) => (
            <LabeledField
              label={
                <span className="sk:flex sk:w-full sk:items-center sk:justify-between">
                  <span>Ship from</span>
                  {modeControl}
                </span>
              }
              error={errors.fulfillment_provider_id?.message}
            >
              <ShipFromSelect
                options={providers}
                value={field.value?.id}
                isLoading={isLoading}
                onChange={id =>
                  onProviderChange({
                    value: {
                      id,
                      service: null,
                      packageValue: null,
                      provider: null,
                      credentials_id: null,
                    },
                  })
                }
              />
            </LabeledField>
          )}
        />

        {shipFromHint ? (
          <span className="sk:-mt-1 sk:text-xs sk:text-fg-tertiary">
            {shipFromHint}
          </span>
        ) : null}

        {packagesSection}

        {enableAutoPrint ? (
          <div className="sk:flex sk:flex-col sk:gap-2">
            <CheckboxField control={control} name="custom_origin">
              Print from a custom origin
            </CheckboxField>
            {useCustomOrigin ? (
              <LabeledField
                label="Origin name"
                error={errors.origin_name?.message}
              >
                <ProviderDropdown
                  required
                  options={customOriginOptions}
                  loading={customOriginLoading}
                  name="origin_name"
                  error={errors.origin_name}
                  sorted={false}
                  placeholder="Origin Name"
                  control={control}
                  defaultValue=""
                  allowAdditions
                  onAddItem={onAddCustomOrigin}
                  fieldClass="sk:mb-0!"
                />
              </LabeledField>
            ) : null}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  );
}
